// ============================================================================
// LIVE EVENT API TYPES
// Types for the Apollo live event endpoints
// ============================================================================

// Scenario configuration - what's active for each scenario
export type ScenarioConfig = {
	label: string
	// Regional events run Thu–Sun. Nationals adds Mon–Wed (6-day Sat→Thu run).
	day:
		| 'thursday'
		| 'friday'
		| 'saturday'
		| 'sunday'
		| 'monday'
		| 'tuesday'
		| 'wednesday'
	active: {
		stream: boolean
		competition: boolean
		classes: boolean
		awards: boolean
		warmup?: boolean
		danceAgain?: boolean
		scores?: boolean
		danceAgainOrder?: boolean
		wrapIt?: boolean
		bonusClasses?: boolean // Friday bonus classes
		danceAgainVod?: boolean // Double showroom VOD announcement
	}
	message?: string // For end-of-day scenarios
}

// API Response: GET /dmapi/event-live-status?eventId={eventId}
export type EventLiveStatusResponse = {
	eventId: string
	isEventLive: boolean
	scenario: string | null
	scenarioConfig: ScenarioConfig | null
	isScheduleReady: boolean
	highlights: string[]
	showDanceAgains: boolean
	showDanceAgainsOrder: boolean
	showWinners?: boolean
	wrapItInstructions: string | null
	wrapItVodUrl: string | null
	danceAgainVodUrl: string | null // Double showroom VOD announcement URL
	fridayEndMessage: string | null
	saturdayEndMessage: string | null
	thursdayEndMessage?: string | null // Thu–Sat events (e.g. Provo)
}

// API Response: GET /dmapi/live-events
export type LiveEventsResponse = {
	events: Array<
		EventLiveStatusResponse & {
			eventName: string
			startDate: string
			endDate: string
		}
	>
}

export type LiveEventWithDetails = LiveEventsResponse['events'][number]

// API Response: GET /dmapi/wrapit-ceremony?eventId={eventId}
export type WrapItCeremonyResponse = {
	eventId: string
	instructions: string | null
	items: Array<{
		order: number
		text: string | null
		routine: {
			order: number
			name: string
			category: string
			studio: string
		} | null
	}>
}

// API Response: GET /dmapi/danceagain?eventId={eventId}
export type DanceAgainResponse = {
	eventId: string
	danceAgains: Array<{
		id: string
		order: number
		name: string
		category: string
		studio: string
	}>
}

// ============================================================================
// ROUTINE TYPES (for competition order display)
// ============================================================================

export type Routine = {
	order: number
	name: string
	ageDivision: string
	style: string
	groupSize: string
	time: string
	day: string
	cancelled: boolean
	studio?: string
	showroom?: string | null // For double showroom events
}

export type RoutinesResponse = {
	eventId: string
	routines: Routine[]
}

// ============================================================================
// EXISTING LIVE EVENT STATUS TYPES (for compatibility with current components)
// ============================================================================

export type LiveStreamInfo = {
	isLive: boolean
	url?: string
	title?: string
	// Double showroom support
	isDoubleShowroom?: boolean
	showrooms?: {
		showroomOne: string | null
		showroomTwo: string | null
	}
	doubleShowroomUrls?: {
		showroomOneUrl: string
		showroomTwoUrl: string
	}
}

export type CompetitionStatus = {
	isActive: boolean
	currentPerformance?: number
	totalPerformances?: number
	currentRoutine?: {
		name: string
		studio: string
		category: string
	}
	scheduleUrl?: string
}

export type AwardsStatus = {
	isActive: boolean
	isStreaming: boolean
	title?: string
	streamUrl?: string
	isWrapIt?: boolean
}

export type ScheduleClass = {
	time: string
	name: string
}

export type ScheduleTrackFull = {
	track: string
	room: string
	classes: ScheduleClass[]
}

export type ClassStatus = {
	isActive: boolean
	highlight?: string
	fullSchedule?: ScheduleTrackFull[]
}

export type WarmUpStatus = {
	isActive: boolean
}

export type ScoresStatus = {
	isReleased: boolean
	message?: string
}

export type DanceAgainPerformer = {
	order: number
	name: string
	studio: string
	category: string
}

export type WrapItCeremonyItem = {
	order: number
	text: string | null
	routine: {
		order: number
		name: string
		category: string
		studio: string
	} | null
}

export type DanceAgainStatus = {
	isAnnounced: boolean
	performanceOrderReleased: boolean
	performers?: DanceAgainPerformer[]
	wrapItItems?: WrapItCeremonyItem[]
	wrapItInstructions?: string
}

export type WinnersStatus = {
	isReleased: boolean
	winnersUrl?: string
}

// Nationals DanceAgain showcase performance order (~40) — curated static JSON
// (data/schedules/{slug}-danceagain-order.json). Same shape used by the stream page.
export type DanceAgainShowcaseOrderItem = {
	order: number
	name: string
	studio: string
	category: string
}

// The format existing components expect
export type LiveEventStatus = {
	isLive: boolean
	eventId: string
	eventName: string
	eventSlug: string
	scenario: string | null
	day:
		| 'thursday'
		| 'friday'
		| 'saturday'
		| 'sunday'
		| 'monday'
		| 'tuesday'
		| 'wednesday'
	stream: LiveStreamInfo
	competition: CompetitionStatus
	awards: AwardsStatus
	classes: ClassStatus
	warmUp: WarmUpStatus
	scores: ScoresStatus
	danceAgain: DanceAgainStatus
	winners: WinnersStatus
	bonusClasses: { isActive: boolean }
	highlights?: string[]
	// New fields from API
	isScheduleReady: boolean
	showDanceAgains: boolean
	showDanceAgainsOrder: boolean
	showWinners: boolean
	// Nationals only: Apollo's independent Solo Winners reveal (Sun night, before
	// the full showWinners completion). Fetched on demand from /dmapi/solo.
	showSolos?: boolean
	wrapItVodUrl?: string
	danceAgainVodUrl?: string // Double showroom VOD announcement URL
	// Nationals only: Tue pre-recorded DanceAgain *selections* announcement VOD
	// (from the static stream config, NOT Apollo — kept separate from
	// danceAgainVodUrl, which is the Wed post-showcase VOD for Nationals).
	danceAgainAnnouncementVodUrl?: string
	// Nationals only: Wed DanceAgain showcase performance order (curated JSON),
	// shown in a sidebar beside the showcase stream.
	danceAgainShowcaseOrder?: DanceAgainShowcaseOrderItem[]
	fridayEndMessage?: string
	saturdayEndMessage?: string
	thursdayEndMessage?: string // Thu–Sat events
	// Competition routines by day (for competition order display)
	routinesByDay?: Record<string, Routine[]>
}

// ============================================================================
// NATIONALS DAY ORDER
// Nationals runs Sat→Thu (6 days). NEVER derive this from getDay() (Sunday=0
// would scramble the order). Use this explicit ordered array for any Nationals
// day iteration (schedule, competition-order tabs). Regional events keep their
// own ['thursday','friday','saturday','sunday'] arrays — do not touch those.
// ============================================================================

export const NATIONALS_DAY_ORDER = [
	'saturday',
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
] as const

export type NationalsDay = (typeof NATIONALS_DAY_ORDER)[number]

// ============================================================================
// STREAM CONFIGURATION - TRUCK ASSIGNMENTS
// Two production trucks (Teal and Grey) with different Resi stream channels
// ============================================================================

type TruckConfig = {
	competitionStreamId: string
	wrapItStreamId: string
}

const TRUCK_CONFIGS: Record<'teal' | 'grey', TruckConfig> = {
	teal: {
		competitionStreamId: '1dc9be52-4f80-48de-a164-f5109b1c6523',
		wrapItStreamId: '2ca42233-213b-4267-9a27-9417683e88a3',
	},
	grey: {
		competitionStreamId: '5a74fc8d-a99b-44f6-94d8-a71ad87e12a1',
		wrapItStreamId: '4636d9b3-9e15-4ae0-9896-7c33b43cc263',
	},
}

// Event slug to truck assignment mapping.
// These are PER-TOUR and must be re-checked every rollover — trucks get
// reassigned between seasons (9 of 32 cities moved from 2025-26 to 2026-27).
// Current: 2026-27 "Fun Again" — 18 teal, 14 grey, 32 total.

// Teal truck events
const TEAL_EVENTS = new Set([
	'anaheim',
	'batonrouge',
	'birmingham',
	'boston',
	'charlotte',
	'chicago',
	'cleveland',
	'columbia',
	'daytonabeach',
	'greenville',
	'lansing',
	'losangeles',
	'mobile',
	'nashville',
	'nationals-myrtlebeach',
	'sanantonio',
	'tulsa',
	'westminster',
])

// Grey truck events
const GREY_EVENTS = new Set([
	'bethesda',
	'branson',
	'charleston',
	'dallas',
	'houston',
	'indianapolis',
	'kansascity',
	'minneapolis',
	'orlando',
	'philadelphia',
	'pittsburgh',
	'provo',
	'richmond',
	'seattle',
])

/**
 * Get the truck assignment for an event
 * @param eventSlug - The event's URL slug
 * @returns 'teal' | 'grey' - defaults to 'teal' for unknown events
 */
export function getTruckForEvent(eventSlug: string): 'teal' | 'grey' {
	if (GREY_EVENTS.has(eventSlug)) return 'grey'
	if (TEAL_EVENTS.has(eventSlug)) return 'teal'
	// Unknown slug: fall back to teal, but say so. A silently-defaulted event
	// streams the wrong truck's channel with no other symptom — this is how
	// Minneapolis went unnoticed through the 2026-27 rollover.
	console.warn(
		`[truck] No truck assignment for event slug "${eventSlug}" — defaulting to teal. Add it to TEAL_EVENTS or GREY_EVENTS in app/utils/live-event.types.ts`,
	)
	return 'teal'
}

/**
 * Get stream IDs for an event based on truck assignment
 */
export function getStreamIdsForEvent(eventSlug: string): TruckConfig {
	const truck = getTruckForEvent(eventSlug)
	return TRUCK_CONFIGS[truck]
}

export function getCompetitionStreamUrl(eventSlug?: string): string {
	const streamIds = getStreamIdsForEvent(eventSlug ?? '')
	return `https://control.resi.io/webplayer/video.html?id=${streamIds.competitionStreamId}`
}

// Per-event Wrap It stream overrides (used when a video tech runs the
// Wrap It ceremony on a non-default channel for a specific event).
const WRAP_IT_STREAM_OVERRIDES: Record<string, string> = {
	// anaheim: '1dc9be52-4f80-48de-a164-f5109b1c6523',
}

export function getWrapItStreamUrl(eventSlug?: string): string {
	const override = eventSlug ? WRAP_IT_STREAM_OVERRIDES[eventSlug] : undefined
	if (override) {
		return `https://control.resi.io/webplayer/video.html?id=${override}`
	}
	const streamIds = getStreamIdsForEvent(eventSlug ?? '')
	return `https://control.resi.io/webplayer/video.html?id=${streamIds.wrapItStreamId}`
}

// ============================================================================
// TRANSFORMER: Convert API response to LiveEventStatus format
// ============================================================================

export function transformToLiveEventStatus(
	apiResponse: LiveEventWithDetails,
	eventSlug: string,
	classSchedule?: ScheduleTrackFull[],
): LiveEventStatus {
	const { scenarioConfig } = apiResponse
	const day = scenarioConfig?.day ?? 'friday'
	const active = scenarioConfig?.active ?? {
		stream: false,
		competition: false,
		classes: false,
		awards: false,
	}

	// Determine stream URL based on what's active and truck assignment
	const isWrapIt = active.wrapIt ?? false
	const streamUrl = isWrapIt
		? getWrapItStreamUrl(eventSlug)
		: getCompetitionStreamUrl(eventSlug)

	// Check for bonus classes - either from active flag OR from scenario name
	// (Apollo may not pass through custom fields, so we also check scenario name)
	const isBonusClassesActive =
		active.bonusClasses ??
		(apiResponse.scenario === 'friday-classes' ||
			apiResponse.scenario === 'thursday-classes')

	// Build the LiveEventStatus object
	return {
		isLive: apiResponse.isEventLive,
		eventId: apiResponse.eventId,
		eventName: apiResponse.eventName,
		eventSlug,
		scenario: apiResponse.scenario,
		day,
		stream: {
			isLive: active.stream ?? false,
			url: active.stream ? streamUrl : undefined,
			title: isWrapIt
				? 'Wrap It Ceremony'
				: active.awards
					? 'Awards Ceremony'
					: 'Competition Live Stream',
		},
		competition: {
			isActive: active.competition ?? false,
			scheduleUrl: `/tour/${eventSlug}/schedule`,
		},
		awards: {
			isActive: active.awards ?? false,
			isStreaming: active.stream && active.awards,
			title: isWrapIt
				? 'Wrap It Ceremony'
				: active.awards
					? `${capitalize(day)} Awards`
					: undefined,
			streamUrl: active.stream && active.awards ? streamUrl : undefined,
			isWrapIt,
		},
		classes: {
			isActive: active.classes ?? false,
			fullSchedule: classSchedule,
		},
		warmUp: {
			isActive: active.warmup ?? false,
		},
		scores: {
			isReleased: active.scores ?? false,
			message: active.scores
				? 'Competition scores are now available in your Apollo account!'
				: undefined,
		},
		danceAgain: {
			// Only use scenario's active.danceAgain - scenario is the source of truth
			isAnnounced: active.danceAgain ?? false,
			performanceOrderReleased: active.danceAgainOrder ?? false,
		},
		winners: {
			isReleased: apiResponse.showWinners ?? false,
			winnersUrl: `/tour/${eventSlug}/results`,
		},
		bonusClasses: {
			isActive: isBonusClassesActive,
		},
		highlights: apiResponse.highlights,
		// Pass through new fields
		isScheduleReady: apiResponse.isScheduleReady,
		showDanceAgains: apiResponse.showDanceAgains,
		showDanceAgainsOrder: apiResponse.showDanceAgainsOrder,
		showWinners: apiResponse.showWinners ?? false,
		wrapItVodUrl: apiResponse.wrapItVodUrl ?? undefined,
		danceAgainVodUrl: apiResponse.danceAgainVodUrl ?? undefined,
		fridayEndMessage: apiResponse.fridayEndMessage ?? undefined,
		saturdayEndMessage: apiResponse.saturdayEndMessage ?? undefined,
		thursdayEndMessage: apiResponse.thursdayEndMessage ?? undefined,
	}
}

// Helper to capitalize first letter
function capitalize(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1)
}

// ============================================================================
// API FETCH HELPERS
//
// Uncached, and only for use inside `fetchLiveEventData`, whose callers already
// wrap it in a cache of its own. Loaders should use the cached equivalents in
// dmapi.server.ts instead — this module is reachable from the client bundle, so
// it can't import the server-only cache directly.
// ============================================================================

export async function fetchLiveEvents(
	apiUrl: string,
): Promise<LiveEventsResponse> {
	const response = await fetch(`${apiUrl}/dmapi/live-events`)
	if (!response.ok) {
		return { events: [] }
	}
	return (await response.json()) as LiveEventsResponse
}

export async function fetchDanceAgains(
	apiUrl: string,
	eventId: string,
): Promise<DanceAgainResponse | null> {
	const response = await fetch(`${apiUrl}/dmapi/danceagain?eventId=${eventId}`)
	if (!response.ok) {
		return null
	}
	return (await response.json()) as DanceAgainResponse
}

export async function fetchWrapItCeremony(
	apiUrl: string,
	eventId: string,
): Promise<WrapItCeremonyResponse | null> {
	const response = await fetch(
		`${apiUrl}/dmapi/wrapit-ceremony?eventId=${eventId}`,
	)
	if (!response.ok) {
		return null
	}
	return (await response.json()) as WrapItCeremonyResponse
}

export async function fetchCompetitionRoutines(
	apiUrl: string,
	eventId: string,
): Promise<Record<string, Routine[]>> {
	const response = await fetch(
		`${apiUrl}/dmapi/competitionDayOrder?eventId=${eventId}`,
	)
	if (!response.ok) {
		return {}
	}

	const data = (await response.json()) as RoutinesResponse
	const routinesByDay: Record<string, Routine[]> = {}

	if (data.routines && data.routines.length > 0) {
		for (const routine of data.routines) {
			if (!routine.day) continue
			const day = routine.day.toLowerCase()
			if (!routinesByDay[day]) {
				routinesByDay[day] = []
			}
			routinesByDay[day].push(routine)
		}
	}

	return routinesByDay
}

// Fetch showSolos (Nationals' independent Solo Winners reveal) from /dmapi/solo.
export async function fetchShowSolos(
	apiUrl: string,
	eventId: string,
): Promise<boolean> {
	try {
		const response = await fetch(`${apiUrl}/dmapi/solo?eventId=${eventId}`)
		if (!response.ok) {
			return false
		}
		const data = (await response.json()) as { showSolos?: boolean }
		return data.showSolos ?? false
	} catch {
		return false
	}
}
